import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Chip, Drawer, Popover, Switch, Tooltip, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaptopIcon from '@mui/icons-material/Laptop';
import RedoIcon from '@mui/icons-material/Redo';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import UndoIcon from '@mui/icons-material/Undo';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import TagFacesIcon from '@mui/icons-material/TagFaces';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ShortTextIcon from '@mui/icons-material/ShortText';
import SubjectIcon from '@mui/icons-material/Subject';
import TitleIcon from '@mui/icons-material/Title';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DrawIcon from '@mui/icons-material/Draw';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ImageIcon from '@mui/icons-material/Image';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { useSnackbar } from 'notistack';

import { useApiRequests, useThemePreference } from 'hooks';
import { useFormBuilderAutosave } from 'hooks/useFormBuilderAutosave/useFormBuilderAutosave';
import { ConsentFormDefinition, ConsentSection, ContentBlock } from 'components/Teleconsent/types';
import { BuilderModeConfig, ConsentFormRenderer } from 'components/Teleconsent/renderer/ConsentFormRenderer';
import { VARIABLE_PREVIEW_CONTEXT, VARIABLE_REGISTRY } from 'components/Teleconsent/variables/variableRegistry';
import { BuilderBlock, BuilderSection, useFormBuilderState } from './hooks/useFormBuilderState';
import styles from './FormBuilderPage.module.scss';

// ─── Palette component definitions ─────────────────────────────────────────

type PaletteItem = { id: string; label: string; icon: React.ReactNode };

const PALETTE_GROUPS: { label: string; items: PaletteItem[] }[] = [
  {
    label: 'Content',
    items: [
      { id: 'paragraph', label: 'Paragraph', icon: <SubjectIcon /> },
      { id: 'header', label: 'Header', icon: <TitleIcon /> },
      { id: 'image', label: 'Image', icon: <ImageIcon /> },
      { id: 'video', label: 'Video', icon: <OndemandVideoIcon /> },
      {
        id: 'divider',
        label: 'Divider',
        icon: (
          <svg width='24' height='14' viewBox='0 0 24 14' fill='none'>
            <line x1='1' y1='7' x2='23' y2='7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Lists',
    items: [
      { id: 'bullet-list', label: 'Bullet', icon: <FormatListBulletedIcon /> },
      { id: 'numbered-list', label: 'Numbered', icon: <FormatListNumberedIcon /> },
      {
        id: 'lettered-list',
        label: 'Lettered',
        icon: (
          <SvgIcon viewBox='0 0 24 24'>
            <text x='1' y='7' fontSize='6' fontFamily='sans-serif' fill='currentColor'>
              a
            </text>
            <rect x='6' y='4.5' width='16' height='2' rx='0.75' fill='currentColor' />
            <text x='1' y='13' fontSize='6' fontFamily='sans-serif' fill='currentColor'>
              b
            </text>
            <rect x='6' y='10.5' width='16' height='2' rx='0.75' fill='currentColor' />
            <text x='1' y='19' fontSize='6' fontFamily='sans-serif' fill='currentColor'>
              c
            </text>
            <rect x='6' y='16.5' width='16' height='2' rx='0.75' fill='currentColor' />
          </SvgIcon>
        ),
      },
    ],
  },
  {
    label: 'Personal Information',
    items: [
      { id: 'field-firstname', label: 'First Name', icon: <ShortTextIcon /> },
      { id: 'field-initials', label: 'Initials', icon: <TitleIcon /> },
      { id: 'field-lastname', label: 'Last Name', icon: <ShortTextIcon /> },
      { id: 'field-dob', label: 'Date of Birth', icon: <CalendarTodayIcon /> },
      { id: 'address', label: 'Address', icon: <HomeOutlinedIcon /> },
      { id: 'field-email', label: 'Email', icon: <EmailOutlinedIcon /> },
      { id: 'field-phone', label: 'Phone Number', icon: <PhoneOutlinedIcon /> },
    ],
  },
  {
    label: 'Date & Time',
    items: [
      { id: 'field-date', label: 'Date', icon: <CalendarTodayIcon /> },
      {
        id: 'field-time',
        label: 'Time',
        icon: (
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          >
            <circle cx='12' cy='12' r='10' />
            <polyline points='12 6 12 12 16 14' />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Input Fields',
    items: [
      { id: 'field-text', label: 'Short Text', icon: <ShortTextIcon /> },
      { id: 'field-textarea', label: 'Long Text', icon: <EditNoteIcon /> },
    ],
  },
  {
    label: 'Selection Fields',
    items: [
      { id: 'field-dropdown', label: 'Dropdown', icon: <ArrowDropDownCircleOutlinedIcon /> },
      { id: 'radio-group', label: 'Radio', icon: <RadioButtonCheckedIcon /> },
      { id: 'checklist', label: 'Checkbox', icon: <CheckBoxOutlinedIcon /> },
    ],
  },
  {
    label: 'Scale',
    items: [
      {
        id: 'scale',
        label: 'Numeric',
        icon: (
          <span style={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 9, fontWeight: 700 }}>
            {['1', '2', '3'].map((n) => (
              <span
                key={n}
                style={{ border: '1px solid currentColor', borderRadius: 2, padding: '0 2px', lineHeight: '14px' }}
              >
                {n}
              </span>
            ))}
          </span>
        ),
      },
      {
        id: 'scale-pain',
        label: 'Emoji',
        icon: <span style={{ fontSize: 11, letterSpacing: -1 }}>😢😐😄</span>,
      },
      { id: 'scale-mood', label: 'Stars', icon: <StarOutlineIcon /> },
      { id: 'scale-stars', label: 'Slider', icon: <LinearScaleIcon /> },
      {
        id: 'scale-likert',
        label: 'Likert',
        icon: (
          <svg width='30' height='20' viewBox='0 0 34 20'>
            <rect x='1' y='2' width='30' height='16' rx='2' fill='none' stroke='currentColor' strokeWidth='1.5' />
            <text
              x='15'
              y='12'
              fontSize='8'
              fontFamily='sans-serif'
              fontWeight='600'
              textAnchor='middle'
              fill='currentColor'
            >
              Agree
            </text>
          </svg>
        ),
      },
      {
        id: 'matrix',
        label: 'Matrix',
        icon: (
          <svg width='24' height='20' viewBox='0 0 24 20'>
            <rect x='1' y='2' width='22' height='16' rx='2' fill='none' stroke='currentColor' strokeWidth='1.5' />
            <line x1='8' y1='2' x2='8' y2='18' stroke='currentColor' strokeWidth='1' />
            <line x1='1' y1='8' x2='23' y2='8' stroke='currentColor' strokeWidth='1' />
            <line x1='1' y1='13' x2='23' y2='13' stroke='currentColor' strokeWidth='1' />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Signing',
    items: [
      { id: 'signature', label: 'Signature Pad', icon: <DrawIcon /> },
      { id: 'initials-pad', label: 'Initials', icon: <EditNoteIcon /> },
      { id: 'print-name', label: 'Print Name', icon: <ShortTextIcon /> },
    ],
  },
  {
    label: 'Internal',
    items: [{ id: 'office-use', label: 'Office Use', icon: <TableChartOutlinedIcon /> }],
  },
];

// ─── Video URL helpers ──────────────────────────────────────────────────────

const SUPPORTED_VIDEO_DOMAINS = ['youtube.com', 'youtu.be', 'vimeo.com'];

function getVideoUrlState(src: string): 'empty' | 'valid' | 'unsupported' {
  if (!src.trim()) return 'empty';
  try {
    const host = new URL(src).hostname.replace(/^www\./, '');
    return SUPPORTED_VIDEO_DOMAINS.includes(host) ? 'valid' : 'unsupported';
  } catch {
    return 'unsupported';
  }
}

// ─── BlockConfigDrawer ──────────────────────────────────────────────────────

type BlockConfigDrawerProps = {
  block: BuilderBlock | null;
  onClose: () => void;
  onUpdate: (patch: Partial<ContentBlock>) => void;
};

function BlockConfigDrawer({ block, onClose, onUpdate }: BlockConfigDrawerProps) {
  const [varAnchor, setVarAnchor] = useState<HTMLElement | null>(null);
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [specialAnchor, setSpecialAnchor] = useState<HTMLElement | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [dragColFrom, setDragColFrom] = useState<number | null>(null);
  const [dragColOver, setDragColOver] = useState<number | null>(null);
  const [dragSfFrom, setDragSfFrom] = useState<number | null>(null);
  const [dragSfOver, setDragSfOver] = useState<number | null>(null);
  const [dragListFrom, setDragListFrom] = useState<number | null>(null);
  const [dragListOver, setDragListOver] = useState<number | null>(null);
  const [dragRowFrom, setDragRowFrom] = useState<number | null>(null);
  const [dragRowOver, setDragRowOver] = useState<number | null>(null);
  const [dragOptFrom, setDragOptFrom] = useState<number | null>(null);
  const [dragOptOver, setDragOptOver] = useState<number | null>(null);

  if (!block) return null;

  const insertVariable = (token: string) => {
    setVarAnchor(null);
    if (block.type === 'paragraph') {
      const el = textAreaRef.current;
      if (el) {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const current = (block.text as string) || '';
        const next = current.slice(0, start) + `{${token}}` + current.slice(end);
        onUpdate({ text: next } as Partial<ContentBlock>);
        setTimeout(() => {
          el.focus();
          const pos = start + token.length + 2;
          el.setSelectionRange(pos, pos);
        }, 0);
      } else {
        const current = (block.text as string) || '';
        onUpdate({ text: current + `{${token}}` } as Partial<ContentBlock>);
      }
    }
  };

  const varGroups = [
    {
      label: 'Provider',
      tokens: [
        'providerName',
        'providerSuffix',
        'providerFirstName',
        'providerLastName',
        'providerTitle',
        'providerSpeciality',
        'providerEmail',
        'providerRoomURL',
      ],
    },
    { label: 'Organization', tokens: ['organizationName'] },
  ].filter((g) => g.tokens.some((t) => t in VARIABLE_REGISTRY));

  const insertAtCursor = (insert: string) => {
    const el = textAreaRef.current;
    const current = (block as { text?: string }).text || '';
    if (el) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = current.slice(0, start) + insert + current.slice(end);
      onUpdate({ text: next } as Partial<ContentBlock>);
      setTimeout(() => {
        el.focus();
        const pos = start + insert.length;
        el.setSelectionRange(pos, pos);
      }, 0);
    } else {
      onUpdate({ text: current + insert } as Partial<ContentBlock>);
    }
  };

  const SPECIAL_CHARS = [
    '©',
    '®',
    '™',
    '°',
    '±',
    '×',
    '÷',
    '§',
    '¶',
    '†',
    '‡',
    '•',
    '…',
    '‰',
    '€',
    '£',
    '¥',
    '¢',
    '←',
    '→',
    '↑',
    '↓',
    '↔',
    '≤',
    '≥',
    '≠',
    '≈',
    '∞',
    '√',
    '∑',
  ];
  const EMOJIS = [
    '😀',
    '😊',
    '😎',
    '😍',
    '😂',
    '🤔',
    '👍',
    '👎',
    '❤️',
    '✅',
    '⚠️',
    '❌',
    'ℹ️',
    '🔔',
    '📋',
    '📝',
    '✍️',
    '📌',
    '🔒',
    '🔓',
  ];

  const pb = block as typeof block & {
    type: 'paragraph';
    text: string;
    fontSize?: number;
    align?: 'left' | 'center' | 'right' | 'justify';
    indent?: number;
    color?: string;
    highlight?: string;
  };
  const updP = (patch: Partial<typeof pb>) => onUpdate(patch as Partial<ContentBlock>);

  const renderParagraphConfig = () => (
    <>
      {/* ── Formatting toolbar ─────────────────────────────── */}
      <div className={styles.drawerField}>
        <div className={styles.drawerLabel}>Formatting</div>
        {/* Row 1: Size + Align + Indent */}
        <div className={styles.fmtRow}>
          <div className={styles.fmtSizeWrapper}>
            <input
              type='number'
              className={styles.fmtSizeInput}
              value={pb.fontSize ?? 14}
              min={8}
              max={96}
              placeholder='px'
              title='Font size (px)'
              onChange={(e) => updP({ fontSize: e.target.value ? parseInt(e.target.value) : 14 })}
            />
            <span className={styles.fmtSizeLabel}>px</span>
          </div>
          <div className={styles.fmtDivider} />
          {(['left', 'center', 'right', 'justify'] as const).map((a) => (
            <button
              key={a}
              title={`Align ${a}`}
              className={`${styles.fmtBtn} ${pb.align === a ? styles.fmtBtnActive : ''}`}
              onClick={() => updP({ align: pb.align === a ? undefined : a })}
            >
              {a === 'left' && <FormatAlignLeftIcon sx={{ fontSize: 16 }} />}
              {a === 'center' && <FormatAlignCenterIcon sx={{ fontSize: 16 }} />}
              {a === 'right' && <FormatAlignRightIcon sx={{ fontSize: 16 }} />}
              {a === 'justify' && <FormatAlignJustifyIcon sx={{ fontSize: 16 }} />}
            </button>
          ))}
          <div className={styles.fmtDivider} />
          <button
            title='Decrease indent'
            className={styles.fmtBtn}
            disabled={!pb.indent}
            onClick={() => updP({ indent: Math.max(0, (pb.indent ?? 0) - 1) || undefined })}
          >
            <FormatIndentDecreaseIcon sx={{ fontSize: 16 }} />
          </button>
          <button
            title='Increase indent'
            className={styles.fmtBtn}
            disabled={(pb.indent ?? 0) >= 5}
            onClick={() => updP({ indent: (pb.indent ?? 0) + 1 })}
          >
            <FormatIndentIncreaseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>

        {/* Row 2: Color + Highlight + Link + Special + Emoji */}
        <div className={styles.fmtRow} style={{ marginTop: 6 }}>
          <label title='Text color' className={styles.fmtColorBtn}>
            <FormatColorTextIcon sx={{ fontSize: 16 }} />
            <span className={styles.fmtColorSwatch} style={{ background: pb.color || '#1a2434' }} />
            <input
              type='color'
              value={pb.color || '#1a2434'}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
              onChange={(e) => updP({ color: e.target.value })}
            />
          </label>
          <label title='Highlight color' className={styles.fmtColorBtn}>
            <FormatColorFillIcon sx={{ fontSize: 16 }} />
            <span
              className={styles.fmtColorSwatch}
              style={{ background: pb.highlight || 'transparent', border: pb.highlight ? 'none' : '1px dashed #aaa' }}
            />
            <input
              type='color'
              value={pb.highlight || '#ffff00'}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
              onChange={(e) => updP({ highlight: e.target.value })}
            />
          </label>
          {pb.highlight && (
            <button title='Remove highlight' className={styles.fmtBtn} onClick={() => updP({ highlight: undefined })}>
              <CloseIcon sx={{ fontSize: 13 }} />
            </button>
          )}
          <div className={styles.fmtDivider} />
          <button
            title='Insert link'
            className={styles.fmtBtn}
            onClick={(e) => {
              setLinkUrl('');
              setLinkText('');
              setLinkAnchor(e.currentTarget);
            }}
          >
            <InsertLinkIcon sx={{ fontSize: 16 }} />
          </button>
          <button
            title='Special character'
            className={styles.fmtBtn}
            onClick={(e) => setSpecialAnchor(e.currentTarget)}
          >
            <span style={{ fontSize: 13, fontWeight: 700 }}>Ω</span>
          </button>
          <button title='Emoji' className={styles.fmtBtn} onClick={(e) => setEmojiAnchor(e.currentTarget)}>
            <TagFacesIcon sx={{ fontSize: 16 }} />
          </button>
          <div className={styles.fmtDivider} />
          <button
            className={styles.variableBtn}
            style={{ fontSize: 11 }}
            onClick={(e) => setVarAnchor(e.currentTarget)}
          >
            {'{ }'} Variable
          </button>
        </div>
      </div>

      {/* ── Content textarea ───────────────────────────────── */}
      <div className={styles.drawerField}>
        <div className={styles.drawerLabel}>Content</div>
        <textarea
          ref={textAreaRef}
          className={styles.drawerInput}
          rows={5}
          value={pb.text || ''}
          onChange={(e) => onUpdate({ text: e.target.value } as Partial<ContentBlock>)}
          placeholder='Enter paragraph text… Use [link text](url) for links'
        />
      </div>

      {/* Variable popover */}
      <Popover
        open={Boolean(varAnchor)}
        anchorEl={varAnchor}
        onClose={() => setVarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div className={styles.variablePopover}>
          {varGroups.map((g) => (
            <div key={g.label}>
              <div className={styles.variableGroup}>{g.label}</div>
              {g.tokens.map((token) => (
                <button key={token} className={styles.variableItem} onClick={() => insertVariable(token)}>
                  {`{${token}}`}
                </button>
              ))}
            </div>
          ))}
        </div>
      </Popover>

      {/* Link popover */}
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{ padding: 12, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--app-text-muted)' }}>Insert Link</div>
          <input
            className={styles.drawerInput}
            placeholder='Display text'
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
          />
          <input
            className={styles.drawerInput}
            placeholder='https://…'
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <button
            className={styles.addOptionBtn}
            style={{ margin: 0 }}
            disabled={!linkUrl.trim()}
            onClick={() => {
              const text = linkText.trim() || linkUrl.trim();
              insertAtCursor(`[${text}](${linkUrl.trim()})`);
              setLinkAnchor(null);
            }}
          >
            Insert
          </button>
        </div>
      </Popover>

      {/* Special character popover */}
      <Popover
        open={Boolean(specialAnchor)}
        anchorEl={specialAnchor}
        onClose={() => setSpecialAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{ padding: 8, display: 'flex', flexWrap: 'wrap', gap: 2, width: 194 }}>
          {SPECIAL_CHARS.map((ch) => (
            <button
              key={ch}
              className={styles.fmtCharBtn}
              onClick={() => {
                insertAtCursor(ch);
                setSpecialAnchor(null);
              }}
            >
              {ch}
            </button>
          ))}
        </div>
      </Popover>

      {/* Emoji popover */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{ padding: 8, display: 'flex', flexWrap: 'wrap', gap: 2, width: 194 }}>
          {EMOJIS.map((em) => (
            <button
              key={em}
              className={styles.fmtCharBtn}
              style={{ fontSize: 18 }}
              onClick={() => {
                insertAtCursor(em);
                setEmojiAnchor(null);
              }}
            >
              {em}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );

  const renderListConfig = () => {
    const listBlock = block as {
      items?: string[];
      listStyle?: string;
      letterCase?: string;
      fontSize?: number;
      indent?: number;
      color?: string;
      highlight?: string;
    };
    const updL = (patch: Partial<typeof listBlock>) => onUpdate(patch as Partial<ContentBlock>);
    const items = listBlock.items || [];
    const isLettered = listBlock.listStyle === 'lettered';
    const isNumbered = listBlock.listStyle === 'numbered' || listBlock.listStyle === 'roman';

    const listDisplayOrder = (() => {
      if (dragListFrom === null || dragListOver === null) return items;
      const arr = [...items];
      const [moved] = arr.splice(dragListFrom, 1);
      arr.splice(dragListOver, 0, moved);
      return arr;
    })();

    const commitListDrop = (from: number, to: number) => {
      if (from === to) return;
      const arr = [...items];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      onUpdate({ items: arr } as Partial<ContentBlock>);
    };

    return (
      <>
        {/* ── Formatting toolbar ─────────────────────────────── */}
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Formatting</div>
          <div className={styles.fmtRow}>
            <div className={styles.fmtSizeWrapper}>
              <input
                type='number'
                className={styles.fmtSizeInput}
                value={listBlock.fontSize ?? 14}
                min={8}
                max={96}
                placeholder='px'
                title='Font size (px)'
                onChange={(e) => updL({ fontSize: e.target.value ? parseInt(e.target.value) : 14 })}
              />
              <span className={styles.fmtSizeLabel}>px</span>
            </div>
            <div className={styles.fmtDivider} />
            <button
              title='Decrease indent'
              className={styles.fmtBtn}
              disabled={!listBlock.indent}
              onClick={() => updL({ indent: Math.max(0, (listBlock.indent ?? 0) - 1) || undefined })}
            >
              <FormatIndentDecreaseIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              title='Increase indent'
              className={styles.fmtBtn}
              disabled={(listBlock.indent ?? 0) >= 5}
              onClick={() => updL({ indent: (listBlock.indent ?? 0) + 1 })}
            >
              <FormatIndentIncreaseIcon sx={{ fontSize: 16 }} />
            </button>
            <div className={styles.fmtDivider} />
            <label title='Text color' className={styles.fmtColorBtn}>
              <FormatColorTextIcon sx={{ fontSize: 16 }} />
              <span className={styles.fmtColorSwatch} style={{ background: listBlock.color || '#1a2434' }} />
              <input
                type='color'
                value={listBlock.color || '#1a2434'}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                onChange={(e) => updL({ color: e.target.value })}
              />
            </label>
            <label title='Highlight color' className={styles.fmtColorBtn}>
              <FormatColorFillIcon sx={{ fontSize: 16 }} />
              <span
                className={styles.fmtColorSwatch}
                style={{
                  background: listBlock.highlight || 'transparent',
                  border: listBlock.highlight ? 'none' : '1px dashed #aaa',
                }}
              />
              <input
                type='color'
                value={listBlock.highlight || '#ffff00'}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                onChange={(e) => updL({ highlight: e.target.value })}
              />
            </label>
            {listBlock.highlight && (
              <button title='Remove highlight' className={styles.fmtBtn} onClick={() => updL({ highlight: undefined })}>
                <CloseIcon sx={{ fontSize: 13 }} />
              </button>
            )}
          </div>
        </div>

        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>List Items</div>
          {listDisplayOrder.map((item, displayIdx) => {
            const origIdx = items.indexOf(item);
            const isDragging = dragListFrom === origIdx;
            return (
              <div
                key={displayIdx}
                className={`${styles.optionRow} ${isDragging ? styles.ouColumnRowDragging : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('listItemIdx', String(origIdx));
                  setDragListFrom(origIdx);
                  setDragListOver(displayIdx);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragListOver(displayIdx);
                }}
                onDragEnd={() => {
                  setDragListFrom(null);
                  setDragListOver(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData('listItemIdx'), 10);
                  if (!isNaN(from)) commitListDrop(from, displayIdx);
                  setDragListFrom(null);
                  setDragListOver(null);
                }}
              >
                <span className={styles.ouColDragHandle} title='Drag to reorder'>
                  ⠿
                </span>
                <input
                  className={styles.optionInput}
                  value={item}
                  onChange={(e) => {
                    const next = [...items];
                    next[origIdx] = e.target.value;
                    onUpdate({ items: next } as Partial<ContentBlock>);
                  }}
                />
                <button
                  className={styles.removeOptionBtn}
                  onClick={() => onUpdate({ items: items.filter((_, j) => j !== origIdx) } as Partial<ContentBlock>)}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            );
          })}
          <button
            className={styles.addOptionBtn}
            onClick={() => onUpdate({ items: [...items, `Item ${items.length + 1}`] } as Partial<ContentBlock>)}
          >
            + Add Item
          </button>
        </div>
        {isNumbered && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Roman Numerals</span>
            <Switch
              checked={listBlock.listStyle === 'roman'}
              onChange={(e) =>
                onUpdate({ listStyle: e.target.checked ? 'roman' : 'numbered' } as Partial<ContentBlock>)
              }
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
        {isLettered && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Uppercase Letters</span>
            <Switch
              checked={listBlock.letterCase === 'upper'}
              onChange={(e) => onUpdate({ letterCase: e.target.checked ? 'upper' : 'lower' } as Partial<ContentBlock>)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
      </>
    );
  };

  const renderFieldGroupConfig = () => {
    if (block.type !== 'field-group') return null;
    const field = block.fields?.[0];
    if (!field) return null;
    const label = 'label' in field ? (field.label as string) : '';
    const placeholder = 'placeholder' in field ? (field as { placeholder?: string }).placeholder ?? '' : '';
    const required = 'required' in field ? (field as { required?: boolean }).required ?? false : false;

    const updateField = (patch: Record<string, unknown>) => {
      const updated = { ...field, ...patch };
      onUpdate({ fields: [updated] } as Partial<ContentBlock>);
    };

    const isDateField = field.type === 'date';
    const isDobField = field.id === 'dob';
    const isGenericDate = isDateField && !isDobField;
    const showPlaceholder = !isDateField && !isDobField && field.type !== 'select';

    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Label</div>
          <input
            className={styles.drawerInput}
            value={label}
            onChange={(e) => updateField({ label: e.target.value })}
          />
        </div>
        {field.type === 'select' && (
          <div className={styles.drawerField}>
            <div className={styles.drawerLabel}>Options</div>
            {(field.options || []).map((opt: { value: string; label: string }, i: number) => (
              <div key={i} className={styles.optionRow}>
                <input
                  className={styles.optionInput}
                  value={opt.label}
                  onChange={(e) => {
                    const next = [...field.options];
                    next[i] = {
                      ...opt,
                      label: e.target.value,
                      value: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                    };
                    updateField({ options: next });
                  }}
                />
                <button
                  className={styles.removeOptionBtn}
                  onClick={() => updateField({ options: field.options.filter((_: unknown, j: number) => j !== i) })}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            ))}
            <button
              className={styles.addOptionBtn}
              onClick={() =>
                updateField({
                  options: [
                    ...(field.options || []),
                    {
                      value: `option${(field.options || []).length + 1}`,
                      label: `Option ${(field.options || []).length + 1}`,
                    },
                  ],
                })
              }
            >
              + Add Option
            </button>
          </div>
        )}
        {showPlaceholder && (
          <div className={styles.drawerField}>
            <div className={styles.drawerLabel}>Placeholder</div>
            <input
              className={styles.drawerInput}
              value={placeholder}
              placeholder='Optional placeholder text'
              onChange={(e) => updateField({ placeholder: e.target.value })}
            />
          </div>
        )}
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch
            checked={required}
            onChange={(e) => updateField({ required: e.target.checked })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        {isGenericDate && (
          <>
            <div className={styles.drawerToggleRow}>
              <span className={styles.drawerToggleLabel}>Default to Today</span>
              <Switch
                checked={!!(field as { defaultToToday?: boolean }).defaultToToday}
                onChange={(e) => updateField({ defaultToToday: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
                }}
              />
            </div>
            <div className={styles.drawerToggleRow}>
              <span className={styles.drawerToggleLabel}>Lock field</span>
              <Switch
                checked={!!(field as { lockField?: boolean }).lockField}
                onChange={(e) => updateField({ lockField: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
                }}
              />
            </div>
          </>
        )}
        {!isDobField && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Short Width</span>
            <Switch
              checked={!!block.halfWidth}
              onChange={(e) => onUpdate({ halfWidth: e.target.checked, rowBreak: false } as Partial<ContentBlock>)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
        {block.halfWidth && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Standalone Row</span>
            <Switch
              checked={!!(block as { rowBreak?: boolean }).rowBreak}
              onChange={(e) => onUpdate({ rowBreak: e.target.checked } as Partial<ContentBlock>)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
      </>
    );
  };

  const renderChecklistConfig = () => {
    if (block.type !== 'checklist') return null;
    const options = block.options || [];
    const isRadioStyle = block.variant === 'radio';
    const allowMultiple = isRadioStyle ? block.multiSelect === true : block.multiSelect !== false;
    const checklistLabel = (block as typeof block & { label?: string }).label ?? '';

    const optDisplayOrder = (() => {
      if (dragOptFrom === null || dragOptOver === null) return options;
      const arr = [...options];
      const [moved] = arr.splice(dragOptFrom, 1);
      arr.splice(dragOptOver, 0, moved);
      return arr;
    })();

    const commitOptDrop = (from: number, to: number) => {
      if (from === to) return;
      const arr = [...options];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      onUpdate({ options: arr } as Partial<ContentBlock>);
    };

    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Label</div>
          <input
            className={styles.drawerInput}
            value={checklistLabel}
            placeholder='Optional label above options'
            onChange={(e) => onUpdate({ label: e.target.value } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Allow Multiple Choice</span>
          <Switch
            checked={allowMultiple}
            onChange={(e) => onUpdate({ multiSelect: e.target.checked } as Partial<ContentBlock>)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Options</div>
          {optDisplayOrder.map((opt, displayIdx) => {
            const origIdx = options.indexOf(opt);
            const isDragging = dragOptFrom === origIdx;
            return (
              <div
                key={opt.id}
                className={`${styles.ouColumnRow} ${isDragging ? styles.ouColumnRowDragging : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('optIdx', String(origIdx));
                  setDragOptFrom(origIdx);
                  setDragOptOver(displayIdx);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOptOver(displayIdx);
                }}
                onDragEnd={() => {
                  setDragOptFrom(null);
                  setDragOptOver(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData('optIdx'), 10);
                  if (!isNaN(from)) commitOptDrop(from, displayIdx);
                  setDragOptFrom(null);
                  setDragOptOver(null);
                }}
              >
                <span className={styles.ouColDragHandle} title='Drag to reorder'>
                  ⠿
                </span>
                <input
                  className={styles.optionInput}
                  value={opt.label}
                  onChange={(e) => {
                    const next = options.map((o, j) => (j === origIdx ? { ...o, label: e.target.value } : o));
                    onUpdate({ options: next } as Partial<ContentBlock>);
                  }}
                />
                <button
                  className={styles.removeOptionBtn}
                  onClick={() =>
                    onUpdate({ options: options.filter((_, j) => j !== origIdx) } as Partial<ContentBlock>)
                  }
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            );
          })}
          <button
            className={styles.addOptionBtn}
            onClick={() =>
              onUpdate({
                options: [...options, { id: `opt-${Date.now()}`, label: `Option ${options.length + 1}` }],
              } as Partial<ContentBlock>)
            }
          >
            + Add Option
          </button>
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch
            checked={!!(block as typeof block & { required?: boolean }).required}
            onChange={(e) => onUpdate({ required: e.target.checked } as Partial<ContentBlock>)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Short Width</span>
          <Switch
            checked={!!(block as typeof block & { halfWidth?: boolean }).halfWidth}
            onChange={(e) => onUpdate({ halfWidth: e.target.checked, rowBreak: false } as Partial<ContentBlock>)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        {(block as typeof block & { halfWidth?: boolean }).halfWidth && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Standalone Row</span>
            <Switch
              checked={!!(block as typeof block & { rowBreak?: boolean }).rowBreak}
              onChange={(e) => onUpdate({ rowBreak: e.target.checked } as Partial<ContentBlock>)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
      </>
    );
  };

  const renderSignatureConfig = () => {
    const sb = block as typeof block & { type: 'signature'; required?: boolean };
    return (
      <>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch
            checked={sb.required !== false}
            onChange={(e) => onUpdate({ required: e.target.checked } as Partial<ContentBlock>)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Include client IP on finalized document</span>
          <Switch defaultChecked />
        </div>
      </>
    );
  };

  const renderInitialsPadConfig = () => {
    if (block.type !== 'initials-pad') return null;
    const ipb = block as typeof block & { type: 'initials-pad'; required?: boolean };
    const rows = ipb.rows || [];

    const rowDisplayOrder = (() => {
      if (dragRowFrom === null || dragRowOver === null) return rows;
      const arr = [...rows];
      const [moved] = arr.splice(dragRowFrom, 1);
      arr.splice(dragRowOver, 0, moved);
      return arr;
    })();

    const commitRowDrop = (from: number, to: number) => {
      if (from === to) return;
      const arr = [...rows];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      onUpdate({ rows: arr } as Partial<ContentBlock>);
    };

    return (
      <>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch
            checked={!!ipb.required}
            onChange={(e) => onUpdate({ required: e.target.checked } as Partial<ContentBlock>)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Acknowledgment Rows</div>
          {rowDisplayOrder.map((row: { id: string; text: string }, displayIdx: number) => {
            const origIdx = rows.indexOf(row);
            const isDragging = dragRowFrom === origIdx;
            return (
              <div
                key={row.id}
                className={`${styles.ouColumnRow} ${isDragging ? styles.ouColumnRowDragging : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('rowIdx', String(origIdx));
                  setDragRowFrom(origIdx);
                  setDragRowOver(displayIdx);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragRowOver(displayIdx);
                }}
                onDragEnd={() => {
                  setDragRowFrom(null);
                  setDragRowOver(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData('rowIdx'), 10);
                  if (!isNaN(from)) commitRowDrop(from, displayIdx);
                  setDragRowFrom(null);
                  setDragRowOver(null);
                }}
              >
                <span className={styles.ouColDragHandle} title='Drag to reorder'>
                  ⠿
                </span>
                <input
                  className={styles.optionInput}
                  value={row.text}
                  onChange={(e) => {
                    const next = rows.map((r: { id: string; text: string }, j: number) =>
                      j === origIdx ? { ...r, text: e.target.value } : r,
                    );
                    onUpdate({ rows: next } as Partial<ContentBlock>);
                  }}
                  placeholder='Acknowledgment text…'
                />
                <button
                  className={styles.removeOptionBtn}
                  onClick={() =>
                    onUpdate({
                      rows: rows.filter((_: { id: string; text: string }, j: number) => j !== origIdx),
                    } as Partial<ContentBlock>)
                  }
                  disabled={rows.length <= 1}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            );
          })}
          <button
            className={styles.addOptionBtn}
            onClick={() =>
              onUpdate({
                rows: [...rows, { id: `row-${Date.now()}`, text: 'I acknowledge…' }],
              } as Partial<ContentBlock>)
            }
          >
            + Add Row
          </button>
        </div>
      </>
    );
  };

  const renderPrintNameConfig = () => {
    if (block.type !== 'print-name') return null;
    const pn = block as typeof block & {
      type: 'print-name';
      nameLabel: string;
      namePlaceholder: string;
      nameRequired: boolean;
      signingAsLabel: string;
      signingAsOptions: { value: string; label: string }[];
      signingAsRequired: boolean;
      acknowledgment: string;
    };
    const upd = (patch: Partial<typeof pn>) => onUpdate(patch as Partial<ContentBlock>);
    const switchSx = {
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
    };
    const opts = pn.signingAsOptions || [];
    const sep = (
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--app-card-outline)',
          margin: '12px 0',
        }}
      />
    );
    return (
      <>
        {/* ── Name field ── */}
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Name Label</div>
          <input
            className={styles.drawerInput}
            value={pn.nameLabel ?? ''}
            onChange={(e) => upd({ nameLabel: e.target.value })}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Placeholder</div>
          <input
            className={styles.drawerInput}
            value={pn.namePlaceholder ?? ''}
            onChange={(e) => upd({ namePlaceholder: e.target.value })}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch checked={!!pn.nameRequired} onChange={(e) => upd({ nameRequired: e.target.checked })} sx={switchSx} />
        </div>

        {sep}

        {/* ── Signing As field ── */}
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Signing As Label</div>
          <input
            className={styles.drawerInput}
            value={pn.signingAsLabel ?? ''}
            onChange={(e) => upd({ signingAsLabel: e.target.value })}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Options</div>
          {opts.map((opt, i) => (
            <div key={i} className={styles.optionRow}>
              <input
                className={styles.optionInput}
                value={opt.label}
                onChange={(e) => {
                  const next = [...opts];
                  next[i] = {
                    ...opt,
                    label: e.target.value,
                    value: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                  };
                  upd({ signingAsOptions: next });
                }}
              />
              <button
                className={styles.removeOptionBtn}
                onClick={() => upd({ signingAsOptions: opts.filter((_, j) => j !== i) })}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
          ))}
          <button
            className={styles.addOptionBtn}
            onClick={() =>
              upd({
                signingAsOptions: [...opts, { value: `option${opts.length + 1}`, label: `Option ${opts.length + 1}` }],
              })
            }
          >
            + Add Option
          </button>
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch
            checked={!!pn.signingAsRequired}
            onChange={(e) => upd({ signingAsRequired: e.target.checked })}
            sx={switchSx}
          />
        </div>

        {sep}

        {/* ── Acknowledgment ── */}
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Acknowledgment</div>
          <textarea
            className={styles.drawerInput}
            rows={3}
            value={pn.acknowledgment ?? ''}
            onChange={(e) => upd({ acknowledgment: e.target.value })}
          />
        </div>
      </>
    );
  };

  const renderOfficeUseConfig = () => {
    if (block.type !== 'office-use') return null;
    const ou = block as typeof block & {
      type: 'office-use';
      label?: string;
      layoutType: 'table' | 'single-row';
      columns: { id: string; label: string; width?: number }[];
    };

    const updateOu = (patch: Partial<typeof ou>) => onUpdate({ ...patch } as Partial<ContentBlock>);

    // Normalize widths, preserving locked columns and distributing remaining % among unlocked
    const normalizeWidths = (cols: typeof ou.columns): typeof ou.columns => {
      const n = cols.length;
      if (n === 0) return cols;
      const lockedTotal = cols.filter((c) => c.locked).reduce((s, c) => s + (c.width ?? 0), 0);
      const unlocked = cols.filter((c) => !c.locked);
      if (unlocked.length === 0) {
        // All locked — just re-distribute evenly ignoring locks
        const base = Math.floor(100 / n);
        const extra = 100 - base * n;
        return cols.map((c, i) => ({ ...c, width: base + (i === n - 1 ? extra : 0) }));
      }
      const remaining = Math.max(0, 100 - lockedTotal);
      const base = Math.floor(remaining / unlocked.length);
      const extra = remaining - base * unlocked.length;
      let extraGiven = false;
      return cols.map((c) => {
        if (c.locked) return c;
        const w = extraGiven ? base : base + extra;
        extraGiven = true;
        return { ...c, width: w };
      });
    };

    const addColumn = () => {
      const next = normalizeWidths([
        ...ou.columns,
        { id: `col-${Date.now()}`, label: `Column ${ou.columns.length + 1}` },
      ]);
      updateOu({ columns: next });
    };

    const removeColumn = (idx: number) => {
      const next = normalizeWidths(ou.columns.filter((_, i) => i !== idx));
      updateOu({ columns: next });
    };

    const updateColumnLabel = (idx: number, label: string) => {
      const next = ou.columns.map((c, i) => (i === idx ? { ...c, label } : c));
      updateOu({ columns: next });
    };

    const updateColumnWidth = (idx: number, newPct: number) => {
      const n = ou.columns.length;
      const clamped = Math.max(1, Math.min(100 - (n - 1), newPct));
      // Distribute remaining % only among unlocked, non-edited columns
      const others = ou.columns.filter((c, i) => i !== idx && !c.locked);
      const lockedOtherTotal = ou.columns
        .filter((c, i) => i !== idx && c.locked)
        .reduce((s, c) => s + (c.width ?? 0), 0);
      const remaining = Math.max(0, 100 - clamped - lockedOtherTotal);
      const perOther = others.length > 0 ? Math.floor(remaining / others.length) : 0;
      const extra = others.length > 0 ? remaining - perOther * others.length : 0;
      let extraGiven = false;
      const next = ou.columns.map((c, i) => {
        if (i === idx) return { ...c, width: clamped };
        if (c.locked) return c;
        if (!extraGiven) {
          extraGiven = true;
          return { ...c, width: perOther + extra };
        }
        return { ...c, width: perOther };
      });
      updateOu({ columns: next });
    };

    const toggleColumnLock = (idx: number) => {
      const next = ou.columns.map((c, i) => (i === idx ? { ...c, locked: !c.locked } : c));
      updateOu({ columns: next });
    };

    // Reorder via drag — mirrors section drag pattern
    const colDisplayOrder = (() => {
      if (dragColFrom === null || dragColOver === null) return ou.columns;
      const arr = [...ou.columns];
      const [moved] = arr.splice(dragColFrom, 1);
      arr.splice(dragColOver, 0, moved);
      return arr;
    })();

    const commitColDrop = (fromIdx: number, toIdx: number) => {
      if (fromIdx === toIdx) return;
      const arr = [...ou.columns];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      updateOu({ columns: arr });
    };

    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Component Label</div>
          <input
            className={styles.drawerInput}
            value={ou.label ?? ''}
            onChange={(e) => updateOu({ label: e.target.value })}
            placeholder='For Office Use'
          />
        </div>

        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Layout Type</div>
          <div className={styles.ouLayoutSelector}>
            <button
              className={`${styles.ouLayoutBtn} ${ou.layoutType === 'table' ? styles.ouLayoutBtnActive : ''}`}
              onClick={() => updateOu({ layoutType: 'table' })}
            >
              <TableChartOutlinedIcon sx={{ fontSize: 16 }} />
              Table
            </button>
            <button
              className={`${styles.ouLayoutBtn} ${ou.layoutType === 'single-row' ? styles.ouLayoutBtnActive : ''}`}
              onClick={() => updateOu({ layoutType: 'single-row' })}
            >
              <ViewStreamOutlinedIcon sx={{ fontSize: 16 }} />
              Single Row
            </button>
          </div>
        </div>

        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Columns</div>
          {colDisplayOrder.map((col, displayIdx) => {
            const originalIdx = ou.columns.indexOf(col);
            const isDragging = dragColFrom === originalIdx;
            return (
              <div
                key={col.id}
                className={`${styles.ouColumnRow} ${isDragging ? styles.ouColumnRowDragging : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('ouColIdx', String(originalIdx));
                  setDragColFrom(originalIdx);
                  setDragColOver(displayIdx);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragColOver(displayIdx);
                }}
                onDragEnd={() => {
                  setDragColFrom(null);
                  setDragColOver(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData('ouColIdx'), 10);
                  if (!isNaN(from)) commitColDrop(from, displayIdx);
                  setDragColFrom(null);
                  setDragColOver(null);
                }}
              >
                <span className={styles.ouColDragHandle} title='Drag to reorder'>
                  ⠿
                </span>
                <input
                  className={styles.optionInput}
                  value={col.label}
                  onChange={(e) => updateColumnLabel(originalIdx, e.target.value)}
                  placeholder={`Column ${displayIdx + 1}`}
                />
                <div className={styles.ouWidthWrapper}>
                  <input
                    type='number'
                    className={styles.ouWidthInput}
                    value={col.width ?? Math.floor(100 / ou.columns.length)}
                    min={1}
                    max={100 - (ou.columns.length - 1)}
                    step={1}
                    title='Width %'
                    onChange={(e) => updateColumnWidth(originalIdx, parseInt(e.target.value, 10) || 1)}
                  />
                  <span className={styles.ouWidthPct}>%</span>
                </div>
                <button
                  className={`${styles.ouLockBtn} ${col.locked ? styles.ouLockBtnActive : ''}`}
                  title={col.locked ? 'Unlock width' : 'Lock width'}
                  onClick={() => toggleColumnLock(originalIdx)}
                >
                  <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2'>
                    {col.locked ? (
                      <>
                        <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
                        <path d='M7 11V7a5 5 0 0110 0v4' />
                      </>
                    ) : (
                      <>
                        <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
                        <path d='M7 11V7a5 5 0 019.9-1' />
                      </>
                    )}
                  </svg>
                </button>
                <button
                  className={styles.removeOptionBtn}
                  onClick={() => removeColumn(originalIdx)}
                  disabled={ou.columns.length <= 1}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            );
          })}
          <button className={styles.addOptionBtn} onClick={addColumn}>
            + Add Column
          </button>
        </div>
      </>
    );
  };

  const renderAddressConfig = () => {
    if (block.type !== 'address') return null;
    const ab = block as typeof block & {
      type: 'address';
      id: string;
      subfields: Array<{
        key: string;
        label: string;
        required: boolean;
        enabled: boolean;
        halfWidth: boolean;
        fieldType: 'text' | 'select-country';
      }>;
    };

    const updateSubfields = (next: typeof ab.subfields) => onUpdate({ subfields: next } as Partial<ContentBlock>);

    const sfDisplay = (() => {
      if (dragSfFrom === null || dragSfOver === null) return ab.subfields;
      const arr = [...ab.subfields];
      const [moved] = arr.splice(dragSfFrom, 1);
      arr.splice(dragSfOver, 0, moved);
      return arr;
    })();

    const commitSfDrop = (from: number, to: number) => {
      if (from === to) return;
      const arr = [...ab.subfields];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      updateSubfields(arr);
    };

    return (
      <div className={styles.drawerField}>
        <div className={styles.drawerLabel}>Address Fields</div>
        {sfDisplay.map((sf, displayIdx) => {
          const origIdx = ab.subfields.indexOf(sf);
          const isDragging = dragSfFrom === origIdx;
          return (
            <div
              key={sf.key}
              className={`${styles.ouColumnRow} ${isDragging ? styles.ouColumnRowDragging : ''}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('sfIdx', String(origIdx));
                setDragSfFrom(origIdx);
                setDragSfOver(displayIdx);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragSfOver(displayIdx);
              }}
              onDragEnd={() => {
                setDragSfFrom(null);
                setDragSfOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const from = parseInt(e.dataTransfer.getData('sfIdx'), 10);
                if (!isNaN(from)) commitSfDrop(from, displayIdx);
                setDragSfFrom(null);
                setDragSfOver(null);
              }}
            >
              <span className={styles.ouColDragHandle} title='Drag to reorder'>
                ⠿
              </span>
              <input
                className={styles.optionInput}
                value={sf.label}
                onChange={(e) => {
                  const next = ab.subfields.map((s, i) => (i === origIdx ? { ...s, label: e.target.value } : s));
                  updateSubfields(next);
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                <span style={{ fontSize: 10, color: 'var(--app-text-muted)' }}>Req</span>
                <input
                  type='checkbox'
                  checked={sf.required}
                  onChange={(e) => {
                    const next = ab.subfields.map((s, i) => (i === origIdx ? { ...s, required: e.target.checked } : s));
                    updateSubfields(next);
                  }}
                />
              </div>
              <button
                className={styles.removeOptionBtn}
                onClick={() => updateSubfields(ab.subfields.filter((_, i) => i !== origIdx))}
                disabled={ab.subfields.length <= 1}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const LIKERT_PRESETS: Record<string, string[]> = {
    Agreement: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    Frequency: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
    Satisfaction: ['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'],
    Importance: ['Not Important', 'Somewhat Important', 'Neutral', 'Important', 'Extremely Important'],
    Quality: ['Very Poor', 'Poor', 'Average', 'Good', 'Excellent'],
  };

  const SWITCH_SX = {
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
  };

  const renderScaleConfig = () => {
    if (block.type !== 'scale') return null;
    const sb = block as typeof block & {
      type: 'scale';
      id: string;
      label: string;
      scaleType: 'numeric' | 'emoji' | 'stars' | 'slider' | 'likert';
      min: number;
      max: number;
      step: number;
      leftLabel: string;
      rightLabel: string;
      required: boolean;
      orientation: 'horizontal' | 'vertical';
      allowHalf: boolean;
      emojiCount: 3 | 5;
      emojiType: 'emoji' | 'colors' | 'clouds' | 'animals' | 'shapes';
      emojiLabels: string[];
      likertOptions: string[];
      halfWidth?: boolean;
      rowBreak?: boolean;
    };

    const upd = (patch: Partial<typeof sb>) => onUpdate(patch as Partial<ContentBlock>);

    const emojiLabels: string[] =
      sb.emojiLabels ??
      (sb.emojiCount === 3 ? ['Sad', 'Neutral', 'Happy'] : ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy']);
    const likertOpts: string[] = sb.likertOptions ?? [
      'Strongly Disagree',
      'Disagree',
      'Neutral',
      'Agree',
      'Strongly Agree',
    ];

    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Scale Label</div>
          <textarea
            className={styles.drawerInput}
            rows={2}
            value={sb.label}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder='Question or title shown above the scale'
          />
        </div>

        {/* ── Numeric ─────────────────────────────── */}
        {sb.scaleType === 'numeric' && (
          <>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Minimum Value</div>
              <input
                type='number'
                className={styles.drawerInput}
                value={sb.min}
                onChange={(e) => upd({ min: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Maximum Value</div>
              <input
                type='number'
                className={styles.drawerInput}
                value={sb.max}
                onChange={(e) => upd({ max: parseFloat(e.target.value) || 10 })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Step Increment</div>
              <input
                type='number'
                className={styles.drawerInput}
                value={sb.step}
                min={0.1}
                step={0.5}
                onChange={(e) => upd({ step: parseFloat(e.target.value) || 1 })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Left Label</div>
              <input
                className={styles.drawerInput}
                value={sb.leftLabel}
                placeholder='e.g. No Pain'
                onChange={(e) => upd({ leftLabel: e.target.value })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Right Label</div>
              <input
                className={styles.drawerInput}
                value={sb.rightLabel}
                placeholder='e.g. Severe Pain'
                onChange={(e) => upd({ rightLabel: e.target.value })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Orientation</div>
              <select
                className={styles.drawerInput}
                value={sb.orientation}
                onChange={(e) => upd({ orientation: e.target.value as 'horizontal' | 'vertical' })}
              >
                <option value='horizontal'>Horizontal</option>
                <option value='vertical'>Vertical</option>
              </select>
            </div>
          </>
        )}

        {/* ── Emoji ───────────────────────────────── */}
        {sb.scaleType === 'emoji' && (
          <>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Emoji Count</div>
              <select
                className={styles.drawerInput}
                value={sb.emojiCount}
                onChange={(e) => {
                  const n = parseInt(e.target.value) as 3 | 5;
                  const defaults =
                    n === 3 ? ['Sad', 'Neutral', 'Happy'] : ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy'];
                  upd({ emojiCount: n, emojiLabels: defaults });
                }}
              >
                <option value={3}>3 Choices</option>
                <option value={5}>5 Choices</option>
              </select>
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Emoji Type</div>
              <select
                className={styles.drawerInput}
                value={sb.emojiType ?? 'emoji'}
                onChange={(e) => upd({ emojiType: e.target.value as typeof sb.emojiType })}
              >
                <option value='emoji'>Emoji</option>
                <option value='colors'>Colors</option>
                <option value='clouds'>Clouds</option>
                <option value='animals'>Animals</option>
                <option value='shapes'>Shapes</option>
              </select>
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Emoji Labels</div>
              {emojiLabels.slice(0, sb.emojiCount).map((lbl, i) => (
                <div key={i} className={styles.optionRow} style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: 'var(--app-text-muted)', minWidth: 56, flexShrink: 0 }}>
                    Emoji {i + 1}
                  </span>
                  <input
                    className={styles.optionInput}
                    value={lbl}
                    onChange={(e) => {
                      const next = [...emojiLabels];
                      next[i] = e.target.value;
                      upd({ emojiLabels: next });
                    }}
                    placeholder={`Label for emoji ${i + 1}`}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Stars ───────────────────────────────── */}
        {sb.scaleType === 'stars' && (
          <>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Star Count</div>
              <input
                type='number'
                className={styles.drawerInput}
                value={sb.max}
                min={2}
                max={10}
                onChange={(e) => upd({ min: 1, max: parseInt(e.target.value) || 5 })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Left Label</div>
              <input
                className={styles.drawerInput}
                value={sb.leftLabel}
                placeholder='e.g. Poor'
                onChange={(e) => upd({ leftLabel: e.target.value })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Right Label</div>
              <input
                className={styles.drawerInput}
                value={sb.rightLabel}
                placeholder='e.g. Excellent'
                onChange={(e) => upd({ rightLabel: e.target.value })}
              />
            </div>
          </>
        )}

        {/* ── Slider ──────────────────────────────── */}
        {sb.scaleType === 'slider' && (
          <>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Minimum Value</div>
              <input
                type='number'
                className={styles.drawerInput}
                value={sb.min}
                onChange={(e) => upd({ min: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Maximum Value</div>
              <input
                type='number'
                className={styles.drawerInput}
                value={sb.max}
                onChange={(e) => upd({ max: parseFloat(e.target.value) || 10 })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Left Label</div>
              <input
                className={styles.drawerInput}
                value={sb.leftLabel}
                placeholder='e.g. Low'
                onChange={(e) => upd({ leftLabel: e.target.value })}
              />
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Right Label</div>
              <input
                className={styles.drawerInput}
                value={sb.rightLabel}
                placeholder='e.g. High'
                onChange={(e) => upd({ rightLabel: e.target.value })}
              />
            </div>
          </>
        )}

        {/* ── Likert ──────────────────────────────── */}
        {sb.scaleType === 'likert' && (
          <>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Likert Presets</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries(LIKERT_PRESETS).map(([name, opts]) => (
                  <button
                    key={name}
                    className={styles.addOptionBtn}
                    style={{ margin: 0 }}
                    onClick={() => {
                      const applied = likertOpts.length <= 3 ? [opts[0], opts[2], opts[4]] : opts;
                      upd({ likertOptions: applied, max: applied.length, min: 1 });
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Likert Count</div>
              <select
                className={styles.drawerInput}
                value={likertOpts.length <= 3 ? 3 : 5}
                onChange={(e) => {
                  const n = parseInt(e.target.value);
                  const defaults3 = ['Disagree', 'Neutral', 'Agree'];
                  const defaults5 = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
                  upd({ likertOptions: n === 3 ? defaults3 : defaults5, max: n, min: 1 });
                }}
              >
                <option value={3}>3 Choices</option>
                <option value={5}>5 Choices</option>
              </select>
            </div>
            <div className={styles.drawerField}>
              <div className={styles.drawerLabel}>Options</div>
              {likertOpts.map((opt, i) => (
                <div key={i} className={styles.optionRow}>
                  <input
                    className={styles.optionInput}
                    value={opt}
                    onChange={(e) => {
                      const next = [...likertOpts];
                      next[i] = e.target.value;
                      upd({ likertOptions: next });
                    }}
                    placeholder={`Option ${i + 1}`}
                  />
                  <button
                    className={styles.removeOptionBtn}
                    onClick={() =>
                      upd({ likertOptions: likertOpts.filter((_, j) => j !== i), max: likertOpts.length - 1 })
                    }
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                </div>
              ))}
              <button
                className={styles.addOptionBtn}
                onClick={() =>
                  upd({ likertOptions: [...likertOpts, `Option ${likertOpts.length + 1}`], max: likertOpts.length + 1 })
                }
              >
                + Add Option
              </button>
            </div>
          </>
        )}

        {/* ── Common ──────────────────────────────── */}
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch checked={sb.required} onChange={(e) => upd({ required: e.target.checked })} sx={SWITCH_SX} />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Short Width</span>
          <Switch
            checked={!!sb.halfWidth}
            onChange={(e) => upd({ halfWidth: e.target.checked, rowBreak: false })}
            sx={SWITCH_SX}
          />
        </div>
        {sb.halfWidth && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Standalone Row</span>
            <Switch checked={!!sb.rowBreak} onChange={(e) => upd({ rowBreak: e.target.checked })} sx={SWITCH_SX} />
          </div>
        )}
      </>
    );
  };

  const MATRIX_PRESETS: Record<string, string[]> = {
    Frequency: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
    Agreement: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
    Satisfaction: ['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'],
    Importance: ['Not Important', 'Somewhat Important', 'Neutral', 'Important', 'Extremely Important'],
  };

  const renderMatrixConfig = () => {
    if (block.type !== 'matrix') return null;
    const mb = block as typeof block & {
      type: 'matrix';
      id: string;
      label?: string;
      columns: string[];
      rows: { id: string; text: string }[];
      required: boolean;
      halfWidth?: boolean;
      rowBreak?: boolean;
    };

    const upd = (patch: Partial<typeof mb>) => onUpdate(patch as Partial<ContentBlock>);

    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Matrix Label</div>
          <input
            className={styles.drawerInput}
            value={mb.label ?? ''}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder='Optional heading above the matrix'
          />
        </div>

        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Column Count</div>
          <select
            className={styles.drawerInput}
            value={mb.columns.length <= 3 ? 3 : 5}
            onChange={(e) => {
              const n = parseInt(e.target.value);
              const defaults3 = ['Never', 'Sometimes', 'Often'];
              const defaults5 = ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'];
              upd({ columns: n === 3 ? defaults3 : defaults5 });
            }}
          >
            <option value={3}>3 Columns</option>
            <option value={5}>5 Columns</option>
          </select>
        </div>

        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Column Presets</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(MATRIX_PRESETS).map(([name, opts]) => (
              <button
                key={name}
                className={styles.addOptionBtn}
                style={{ margin: 0 }}
                onClick={() => {
                  const applied = mb.columns.length <= 3 ? [opts[0], opts[2], opts[4]] : opts;
                  upd({ columns: applied });
                }}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Columns</div>
          {mb.columns.map((col, i) => (
            <div key={i} className={styles.optionRow}>
              <input
                className={styles.optionInput}
                value={col}
                onChange={(e) => {
                  const next = [...mb.columns];
                  next[i] = e.target.value;
                  upd({ columns: next });
                }}
                placeholder={`Column ${i + 1}`}
              />
              <button
                className={styles.removeOptionBtn}
                onClick={() => upd({ columns: mb.columns.filter((_, j) => j !== i) })}
                disabled={mb.columns.length <= 2}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
          ))}
          <button
            className={styles.addOptionBtn}
            onClick={() => upd({ columns: [...mb.columns, `Option ${mb.columns.length + 1}`] })}
          >
            + Add Column
          </button>
        </div>

        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Rows (Statements)</div>
          {mb.rows.map((row, i) => (
            <div key={row.id} className={styles.optionRow}>
              <input
                className={styles.optionInput}
                value={row.text}
                onChange={(e) => {
                  const next = mb.rows.map((r, j) => (j === i ? { ...r, text: e.target.value } : r));
                  upd({ rows: next });
                }}
                placeholder={`Statement ${i + 1}`}
              />
              <button
                className={styles.removeOptionBtn}
                onClick={() => upd({ rows: mb.rows.filter((_, j) => j !== i) })}
                disabled={mb.rows.length <= 1}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
          ))}
          <button
            className={styles.addOptionBtn}
            onClick={() => upd({ rows: [...mb.rows, { id: `row-${Date.now()}`, text: '' }] })}
          >
            + Add Row
          </button>
        </div>

        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch checked={mb.required} onChange={(e) => upd({ required: e.target.checked })} sx={SWITCH_SX} />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Short Width</span>
          <Switch
            checked={!!mb.halfWidth}
            onChange={(e) => upd({ halfWidth: e.target.checked, rowBreak: false })}
            sx={SWITCH_SX}
          />
        </div>
        {mb.halfWidth && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Standalone Row</span>
            <Switch checked={!!mb.rowBreak} onChange={(e) => upd({ rowBreak: e.target.checked })} sx={SWITCH_SX} />
          </div>
        )}
      </>
    );
  };

  const renderTimeConfig = () => {
    if (block.type !== 'time-field') return null;
    const tb = block as typeof block & {
      type: 'time-field';
      id: string;
      label: string;
      required: boolean;
      use24hr: boolean;
      halfWidth?: boolean;
      rowBreak?: boolean;
    };
    const upd = (patch: Partial<typeof tb>) => onUpdate(patch as Partial<ContentBlock>);
    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Time Label</div>
          <input
            className={styles.drawerInput}
            value={tb.label}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder='e.g. Appointment Time'
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>24-Hour Format</span>
          <Switch
            checked={tb.use24hr}
            onChange={(e) => upd({ use24hr: e.target.checked })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Required</span>
          <Switch
            checked={tb.required}
            onChange={(e) => upd({ required: e.target.checked })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Short Width</span>
          <Switch
            checked={!!tb.halfWidth}
            onChange={(e) => upd({ halfWidth: e.target.checked, rowBreak: false })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        {tb.halfWidth && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Standalone Row</span>
            <Switch
              checked={!!tb.rowBreak}
              onChange={(e) => upd({ rowBreak: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
      </>
    );
  };

  const renderImageConfig = () => {
    if (block.type !== 'image') return null;
    const ib = block;
    const upd = (patch: Partial<ContentBlock>) => onUpdate(patch);
    const MAX_HEIGHT_OPTIONS: { label: string; value: number | undefined }[] = [
      { label: 'None', value: undefined },
      { label: '200 px', value: 200 },
      { label: '400 px', value: 400 },
      { label: '600 px', value: 600 },
    ];
    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Image URL</div>
          <input
            className={styles.drawerInput}
            value={ib.src}
            placeholder='https://example.com/image.png'
            onChange={(e) => upd({ src: e.target.value } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Alt Text / Caption</div>
          <input
            className={styles.drawerInput}
            value={ib.alt ?? ''}
            placeholder='Describe the image'
            onChange={(e) => upd({ alt: e.target.value } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Alignment</div>
          <select
            className={styles.drawerInput}
            value={ib.alignment ?? 'center'}
            onChange={(e) => upd({ alignment: e.target.value as 'left' | 'center' | 'right' } as Partial<ContentBlock>)}
          >
            <option value='left'>Left</option>
            <option value='center'>Center</option>
            <option value='right'>Right</option>
          </select>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Width</div>
          <select
            className={styles.drawerInput}
            value={String(ib.widthPercent ?? 100)}
            onChange={(e) => upd({ widthPercent: Number(e.target.value) } as Partial<ContentBlock>)}
          >
            <option value='25'>25%</option>
            <option value='50'>50%</option>
            <option value='75'>75%</option>
            <option value='100'>Full</option>
          </select>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Max Height</div>
          <select
            className={styles.drawerInput}
            value={String(ib.maxHeight ?? '')}
            onChange={(e) => {
              const v = e.target.value;
              upd({ maxHeight: v === '' ? undefined : Number(v) } as Partial<ContentBlock>);
            }}
          >
            {MAX_HEIGHT_OPTIONS.map((o) => (
              <option key={String(o.value)} value={String(o.value ?? '')}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Top Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={(ib as typeof ib & { marginTop?: number }).marginTop ?? 15}
            min={0}
            max={80}
            onChange={(e) => upd({ marginTop: parseInt(e.target.value) || 0 } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Bottom Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={(ib as typeof ib & { marginBottom?: number }).marginBottom ?? 15}
            min={0}
            max={80}
            onChange={(e) => upd({ marginBottom: parseInt(e.target.value) || 0 } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Short Width</span>
          <Switch
            checked={!!ib.halfWidth}
            onChange={(e) => upd({ halfWidth: e.target.checked, rowBreak: false } as Partial<ContentBlock>)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        {ib.halfWidth && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Standalone Row</span>
            <Switch
              checked={!!ib.rowBreak}
              onChange={(e) => upd({ rowBreak: e.target.checked } as Partial<ContentBlock>)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
      </>
    );
  };

  const renderVideoConfig = () => {
    if (block.type !== 'video') return null;
    const vb = block;
    const upd = (patch: Partial<ContentBlock>) => onUpdate(patch);
    const urlState = getVideoUrlState(vb.src);
    const MAX_HEIGHT_OPTIONS: { label: string; value: number | undefined }[] = [
      { label: 'None', value: undefined },
      { label: '200 px', value: 200 },
      { label: '400 px', value: 400 },
      { label: '600 px', value: 600 },
    ];
    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Video URL</div>
          <input
            className={styles.drawerInput}
            value={vb.src}
            placeholder='https://www.youtube.com/watch?v=...'
            onChange={(e) => upd({ src: e.target.value } as Partial<ContentBlock>)}
          />
          <div style={{ fontSize: 11, color: 'var(--app-text-muted)', marginTop: 4 }}>
            Supported: YouTube and Vimeo public links
          </div>
          {urlState === 'valid' && (
            <div style={{ fontSize: 11, color: '#3bb8a4', marginTop: 2 }}>✓ Valid video URL</div>
          )}
          {urlState === 'unsupported' && (
            <div style={{ fontSize: 11, color: '#e6a817', marginTop: 2 }}>
              ⚠ Unsupported platform — only YouTube and Vimeo links will embed
            </div>
          )}
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Caption</div>
          <input
            className={styles.drawerInput}
            value={vb.caption ?? ''}
            placeholder='Optional caption'
            onChange={(e) => upd({ caption: e.target.value } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Aspect Ratio</div>
          <select
            className={styles.drawerInput}
            value={vb.aspectRatio ?? '16:9'}
            onChange={(e) => upd({ aspectRatio: e.target.value as '16:9' | '4:3' | '1:1' } as Partial<ContentBlock>)}
          >
            <option value='16:9'>16:9 (Widescreen)</option>
            <option value='4:3'>4:3 (Standard)</option>
            <option value='1:1'>1:1 (Square)</option>
          </select>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Max Height</div>
          <select
            className={styles.drawerInput}
            value={String(vb.maxHeight ?? '')}
            onChange={(e) => {
              const v = e.target.value;
              upd({ maxHeight: v === '' ? undefined : Number(v) } as Partial<ContentBlock>);
            }}
          >
            {MAX_HEIGHT_OPTIONS.map((o) => (
              <option key={String(o.value)} value={String(o.value ?? '')}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Top Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={(vb as typeof vb & { marginTop?: number }).marginTop ?? 15}
            min={0}
            max={80}
            onChange={(e) => upd({ marginTop: parseInt(e.target.value) || 0 } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Bottom Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={(vb as typeof vb & { marginBottom?: number }).marginBottom ?? 15}
            min={0}
            max={80}
            onChange={(e) => upd({ marginBottom: parseInt(e.target.value) || 0 } as Partial<ContentBlock>)}
          />
        </div>
        <div className={styles.drawerToggleRow}>
          <span className={styles.drawerToggleLabel}>Short Width</span>
          <Switch
            checked={!!vb.halfWidth}
            onChange={(e) => upd({ halfWidth: e.target.checked, rowBreak: false } as Partial<ContentBlock>)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
            }}
          />
        </div>
        {vb.halfWidth && (
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Standalone Row</span>
            <Switch
              checked={!!vb.rowBreak}
              onChange={(e) => upd({ rowBreak: e.target.checked } as Partial<ContentBlock>)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
        )}
      </>
    );
  };

  const renderHeaderConfig = () => {
    if (block.type !== 'header') return null;
    const hb = block as typeof block & {
      type: 'header';
      text: string;
      fontSize?: number;
      color?: string;
      marginTop?: number;
      marginBottom?: number;
    };
    const upd = (patch: Partial<typeof hb>) => onUpdate(patch as Partial<ContentBlock>);
    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Header Text</div>
          <input
            className={styles.drawerInput}
            value={hb.text}
            onChange={(e) => upd({ text: e.target.value })}
            placeholder='Enter header text'
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Size (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={hb.fontSize ?? 30}
            min={8}
            max={96}
            onChange={(e) => upd({ fontSize: parseInt(e.target.value) || 30 })}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Color</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type='color'
              value={hb.color || '#1a2434'}
              onChange={(e) => upd({ color: e.target.value })}
              style={{
                width: 40,
                height: 34,
                padding: 2,
                border: '1px solid var(--app-field-outline)',
                borderRadius: 6,
                cursor: 'pointer',
                background: 'none',
              }}
            />
            <input
              className={styles.drawerInput}
              value={hb.color || ''}
              onChange={(e) => upd({ color: e.target.value })}
              placeholder='#1a2434'
              style={{ flex: 1 }}
            />
          </div>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Top Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={hb.marginTop ?? 8}
            min={0}
            max={80}
            onChange={(e) => upd({ marginTop: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Bottom Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={hb.marginBottom ?? 8}
            min={0}
            max={80}
            onChange={(e) => upd({ marginBottom: parseInt(e.target.value) || 0 })}
          />
        </div>
      </>
    );
  };

  const renderDividerConfig = () => {
    if (block.type !== 'divider') return null;
    const db = block as typeof block & {
      type: 'divider';
      thickness?: number;
      lengthPercent?: number;
      lineType?: 'solid' | 'dotted' | 'dashed' | 'double';
      color?: string;
      marginTop?: number;
      marginBottom?: number;
    };
    const upd = (patch: Partial<typeof db>) => onUpdate(patch as Partial<ContentBlock>);
    return (
      <>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Thickness (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={db.thickness ?? 1}
            min={1}
            max={16}
            onChange={(e) => upd({ thickness: parseInt(e.target.value) || 1 })}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Length (%)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={db.lengthPercent ?? 100}
            min={10}
            max={100}
            step={5}
            onChange={(e) => upd({ lengthPercent: parseInt(e.target.value) || 100 })}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Type</div>
          <select
            className={styles.drawerInput}
            value={db.lineType ?? 'solid'}
            onChange={(e) => upd({ lineType: e.target.value as 'solid' | 'dotted' | 'dashed' | 'double' })}
          >
            <option value='solid'>Solid</option>
            <option value='dotted'>Dotted</option>
            <option value='dashed'>Dashed</option>
            <option value='double'>Double</option>
          </select>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Color</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type='color'
              value={db.color ?? '#e2e8f0'}
              onChange={(e) => upd({ color: e.target.value })}
              style={{
                width: 40,
                height: 34,
                padding: 2,
                border: '1px solid var(--app-field-outline)',
                borderRadius: 6,
                cursor: 'pointer',
                background: 'none',
              }}
            />
            <input
              className={styles.drawerInput}
              value={db.color ?? '#e2e8f0'}
              onChange={(e) => upd({ color: e.target.value })}
              placeholder='#e2e8f0'
              style={{ flex: 1 }}
            />
          </div>
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Top Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={db.marginTop ?? 16}
            min={0}
            max={80}
            onChange={(e) => upd({ marginTop: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div className={styles.drawerField}>
          <div className={styles.drawerLabel}>Bottom Margin (px)</div>
          <input
            type='number'
            className={styles.drawerInput}
            value={db.marginBottom ?? 16}
            min={0}
            max={80}
            onChange={(e) => upd({ marginBottom: parseInt(e.target.value) || 0 })}
          />
        </div>
      </>
    );
  };

  const blockTypeLabel: Record<string, string> = {
    paragraph: 'Paragraph / Text',
    'bullet-list': 'List',
    'field-group': 'Field',
    'time-field': 'Time',
    checklist: 'Checkbox / Radio Group',
    signature: 'Signature Pad',
    'initials-pad': 'Initials',
    'print-name': 'Print Name',
    callout: 'Callout',
    'office-use': 'Office Use',
    address: 'Address',
    scale: 'Scale',
    matrix: 'Matrix',
    image: 'Image',
    video: 'Video',
    header: 'Header',
    divider: 'Divider',
  };

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={onClose}
      hideBackdrop
      variant='persistent'
      PaperProps={{
        sx: {
          background: 'var(--app-surface)',
          boxShadow: '-6px 0 24px rgba(0,0,0,0.10)',
          border: '1px solid var(--app-card-outline)',
        },
      }}
    >
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <Typography className={styles.drawerTitle}>{blockTypeLabel[block.type] ?? 'Block'} Settings</Typography>
          <button className={styles.drawerCloseBtn} onClick={onClose}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
        <div className={styles.drawerBody}>
          {block.type === 'paragraph' && renderParagraphConfig()}
          {block.type === 'header' && renderHeaderConfig()}
          {block.type === 'bullet-list' && renderListConfig()}
          {block.type === 'field-group' && renderFieldGroupConfig()}
          {block.type === 'checklist' && renderChecklistConfig()}
          {block.type === 'signature' && renderSignatureConfig()}
          {block.type === 'initials-pad' && renderInitialsPadConfig()}
          {block.type === 'print-name' && renderPrintNameConfig()}
          {block.type === 'office-use' && renderOfficeUseConfig()}
          {block.type === 'address' && renderAddressConfig()}
          {block.type === 'time-field' && renderTimeConfig()}
          {block.type === 'scale' && renderScaleConfig()}
          {block.type === 'matrix' && renderMatrixConfig()}
          {block.type === 'image' && renderImageConfig()}
          {block.type === 'video' && renderVideoConfig()}
          {block.type === 'divider' && renderDividerConfig()}
        </div>
      </div>
    </Drawer>
  );
}

// ─── SectionConfigDrawer ────────────────────────────────────────────────────

type SectionConfigDrawerProps = {
  section: {
    _id: string;
    heading: string;
    navLabelOverride: string;
    sectionTitle: string;
    hideSectionTitle: boolean;
  } | null;
  onClose: () => void;
  onUpdate: (patch: {
    heading?: string;
    navLabelOverride?: string;
    sectionTitle?: string;
    hideSectionTitle?: boolean;
  }) => void;
};

function SectionConfigDrawer({ section, onClose, onUpdate }: SectionConfigDrawerProps) {
  if (!section) return null;
  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={onClose}
      hideBackdrop
      variant='persistent'
      PaperProps={{
        sx: {
          background: 'var(--app-surface)',
          boxShadow: '-6px 0 24px rgba(0,0,0,0.10)',
          border: '1px solid var(--app-card-outline)',
        },
      }}
    >
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <Typography className={styles.drawerTitle}>Section Settings</Typography>
          <button className={styles.drawerCloseBtn} onClick={onClose}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
        <div className={styles.drawerBody}>
          <div className={styles.drawerField}>
            <div className={styles.drawerLabel}>Section Title</div>
            <input
              className={styles.drawerInput}
              value={section.sectionTitle}
              onChange={(e) => onUpdate({ sectionTitle: e.target.value })}
              placeholder='e.g. SECTION 1'
              disabled={section.hideSectionTitle}
            />
            <div style={{ fontSize: 11, color: 'var(--app-text-muted)', marginTop: 3 }}>
              Replaces the default "SECTION 1" label shown above the heading
            </div>
          </div>
          <div className={styles.drawerToggleRow}>
            <span className={styles.drawerToggleLabel}>Hide Section Title</span>
            <Switch
              checked={section.hideSectionTitle}
              onChange={(e) => onUpdate({ hideSectionTitle: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3bb8a4', opacity: 1 },
              }}
            />
          </div>
          <div className={styles.drawerField}>
            <div className={styles.drawerLabel}>Section Heading</div>
            <input
              className={styles.drawerInput}
              value={section.heading}
              onChange={(e) => onUpdate({ heading: e.target.value })}
              placeholder='Section heading text'
            />
          </div>
          <div className={styles.drawerField}>
            <div className={styles.drawerLabel}>Nav Bar Label</div>
            <input
              className={styles.drawerInput}
              value={section.navLabelOverride}
              onChange={(e) => onUpdate({ navLabelOverride: e.target.value })}
              placeholder='Defaults to section heading'
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

// ─── ComponentPalette ───────────────────────────────────────────────────────

function ComponentPalette() {
  const initialOpen = Object.fromEntries(PALETTE_GROUPS.map((g) => [g.label, true]));
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initialOpen);

  const toggleGroup = (label: string) => setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  return (
    <div className={styles.palette}>
      <Typography className={styles.palettePanelTitle}>Components</Typography>
      {PALETTE_GROUPS.map((group) => (
        <div key={group.label} className={styles.paletteGroup}>
          <div className={styles.paletteGroupHeader} onClick={() => toggleGroup(group.label)}>
            <Typography className={styles.paletteGroupLabel}>{group.label}</Typography>
            <ExpandMoreIcon className={`${styles.paletteGroupChevron} ${openGroups[group.label] ? styles.open : ''}`} />
          </div>
          {openGroups[group.label] && (
            <div className={styles.paletteGrid}>
              {group.items.map((item) => (
                <div
                  key={item.id}
                  className={styles.paletteChip}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('blockType', item.id)}
                >
                  <span className={styles.paletteChipIcon}>{item.icon}</span>
                  <span className={styles.paletteChipLabel}>{item.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function sectionsToConsentSections(builderSections: BuilderSection[]): ConsentSection[] {
  return builderSections.map((s, i) => ({
    id: s._id,
    navLabel: s.navLabelOverride.trim() || `Section ${i + 1}`,
    heading: s.heading,
    sectionTitle: s.sectionTitle || undefined,
    hideSectionTitle: s.hideSectionTitle || undefined,
    blocks: s.blocks,
  }));
}

// ─── FormBuilderPage ─────────────────────────────────────────────────────────

export function FormBuilderPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { getCustomConsentForm, deleteCustomConsentForm } = useApiRequests();
  const { resolvedThemeMode } = useThemePreference();

  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // selectedBlockInfo stores { sectionId, blockIndex } — the index matches what the renderer uses for bId
  const [selectedBlockInfo, setSelectedBlockInfo] = useState<{ sectionId: string; blockIndex: number } | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'laptop' | 'mobile'>('desktop');
  const [dragSectionFrom, setDragSectionFrom] = useState<number | null>(null);
  const [dragSectionOver, setDragSectionOver] = useState<number | null>(null);

  const builderState = useFormBuilderState();
  const {
    settings,
    sections,
    canUndo,
    canRedo,
    undo,
    redo,
    updateSettings,
    hydrateSections,
    addSection,
    removeSection,
    updateSection: _updateSection,
    reorderSections,
    addBlock,
    duplicateBlock,
    removeBlock,
    updateBlock,
    reorderBlocks,
    moveBlock,
    serialize,
  } = builderState;

  const getValueRef = useRef(serialize);
  useEffect(() => {
    getValueRef.current = serialize;
  }, [serialize]);
  const getValue = useCallback(() => getValueRef.current(), []);

  const { saveState, schedule } = useFormBuilderAutosave(formId!, getValue);

  // Load existing form on mount
  useEffect(() => {
    if (!formId) return;
    getCustomConsentForm(formId)
      .then((detail) => {
        let parsed: { sections?: unknown[]; requiredFields?: string[]; headerSubtitle?: string } = {};
        try {
          parsed = JSON.parse(detail.definition) as typeof parsed;
        } catch {
          /* ignore */
        }

        updateSettings({
          title: detail.title,
          subtitle: detail.subtitle ?? '',
          intro: detail.intro ?? '',
          pdfTitle: detail.pdfTitle,
          includeOfficeUseTable: detail.includeOfficeUseTable,
          isEnabled: detail.isEnabled,
          headerSubtitle: parsed.headerSubtitle ?? '',
        });

        if (parsed.sections && Array.isArray(parsed.sections)) {
          hydrateSections(parsed.sections as ConsentSection[]);
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to load form', { variant: 'error' });
      })
      .finally(() => setLoading(false));
  }, [formId]); // eslint-disable-line

  // Keyboard shortcuts for undo / redo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  // Trigger autosave on every settings/sections change (skip initial load)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    schedule();
  }, [settings, sections]); // eslint-disable-line

  // Live definition passed to ConsentFormRenderer for builder preview
  const liveDefinition = useMemo<ConsentFormDefinition>(
    () => ({
      formKey: formId ?? 'preview',
      title: settings.title || 'Untitled Form',
      subtitle: settings.subtitle || undefined,
      intro: settings.intro || undefined,
      headerSubtitle: settings.headerSubtitle || undefined,
      pdfTitle: settings.pdfTitle || settings.title,
      sections: sectionsToConsentSections(sections),
      requiredFields: [],
    }),
    [formId, settings, sections],
  );

  // Selected block derived from (sectionId, blockIndex)
  const selectedBlock = useMemo<BuilderBlock | null>(() => {
    if (!selectedBlockInfo) return null;
    const section = sections.find((s) => s._id === selectedBlockInfo.sectionId);
    return section?.blocks[selectedBlockInfo.blockIndex] ?? null;
  }, [selectedBlockInfo, sections]);

  const builderConfig = useMemo<BuilderModeConfig>(
    () => ({
      selectedBlockKey: selectedBlockInfo ? `${selectedBlockInfo.sectionId}-${selectedBlockInfo.blockIndex}` : null,
      onBlockClick: (sectionId, blockIndex) => {
        setSelectedBlockInfo((prev) =>
          prev?.sectionId === sectionId && prev.blockIndex === blockIndex ? null : { sectionId, blockIndex },
        );
        setEditingSectionId(null);
      },
      onBlockDelete: (sectionId, blockIndex) => {
        const section = sections.find((s) => s._id === sectionId);
        const block = section?.blocks[blockIndex];
        if (block) {
          removeBlock(sectionId, block._id);
          if (selectedBlockInfo?.sectionId === sectionId && selectedBlockInfo.blockIndex === blockIndex) {
            setSelectedBlockInfo(null);
          }
        }
      },
      onBlockDuplicate: (sectionId, blockIndex) => {
        const section = sections.find((s) => s._id === sectionId);
        const block = section?.blocks[blockIndex];
        if (block) duplicateBlock(sectionId, block._id);
      },
      onBlockMove: (sectionId, blockIndex, direction) => {
        const toIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
        reorderBlocks(sectionId, blockIndex, toIndex);
        setSelectedBlockInfo((prev) => {
          if (!prev || prev.sectionId !== sectionId || prev.blockIndex !== blockIndex) return prev;
          return { sectionId, blockIndex: toIndex };
        });
      },
      onSectionEdit: (sectionId) => {
        setEditingSectionId(sectionId);
        setSelectedBlockInfo(null);
      },
      onSectionDelete: (sectionId) => {
        removeSection(sectionId);
        if (selectedBlockInfo?.sectionId === sectionId) setSelectedBlockInfo(null);
        if (editingSectionId === sectionId) setEditingSectionId(null);
      },
      onSectionMove: (sectionId, direction) => {
        const idx = sections.findIndex((s) => s._id === sectionId);
        if (idx < 0) return;
        const toIdx = direction === 'up' ? idx - 1 : idx + 1;
        reorderSections(idx, toIdx);
      },
      onDropBlock: (sectionId, blockType, atIndex) => {
        addBlock(sectionId, blockType, atIndex);
      },
      onBlockDragReorder: (fromSectionId, fromBlockIndex, toSectionId, toIndex) => {
        if (fromSectionId === toSectionId) {
          reorderBlocks(fromSectionId, fromBlockIndex, toIndex);
        } else {
          const fromSection = sections.find((s) => s._id === fromSectionId);
          const block = fromSection?.blocks[fromBlockIndex];
          if (block) moveBlock(fromSectionId, block._id, toSectionId, toIndex);
        }
      },
      isMobilePreview: previewDevice === 'mobile',
      totalSections: sections.length,
      onNavLabelChange: (sectionId, label) => {
        _updateSection(sectionId, { navLabelOverride: label });
      },
    }),
    [
      sections,
      selectedBlockInfo,
      previewDevice,
      removeBlock,
      duplicateBlock,
      reorderBlocks,
      reorderSections,
      removeSection,
      addBlock,
      moveBlock,
    ],
  );

  // Reordered section list for live drag preview — reflects the drag position before drop
  const sectionDisplayOrder = useMemo(() => {
    if (dragSectionFrom === null || dragSectionOver === null) return sections;
    const arr = [...sections];
    const [moved] = arr.splice(dragSectionFrom, 1);
    arr.splice(dragSectionOver, 0, moved);
    return arr;
  }, [sections, dragSectionFrom, dragSectionOver]);

  const deviceMaxWidth: Record<string, number> = { desktop: 720, laptop: 580, mobile: 375 };

  const saveStateLabel: Record<string, string> = {
    idle: 'Autosave on',
    saving: 'Saving…',
    saved: 'Saved ✓',
    error: 'Save failed',
  };
  const saveStateClass: Record<string, string> = {
    idle: styles.saveAutosaveOn,
    saving: styles.saveSaving,
    saved: styles.saveSaved,
    error: styles.saveError,
  };

  if (loading) return null;

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate('/settings#teleconsent')}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            Back to Settings
          </button>
          <div className={styles.headerDivider} />
          <input
            className={styles.titleInput}
            value={settings.title}
            onChange={(e) => updateSettings({ title: e.target.value })}
            placeholder='Form title…'
          />
        </div>

        <div className={styles.headerCenter}>
          <Tooltip title='Desktop'>
            <button
              className={`${styles.deviceBtn} ${previewDevice === 'desktop' ? styles.deviceBtnActive : ''}`}
              onClick={() => setPreviewDevice('desktop')}
            >
              <DesktopWindowsOutlinedIcon sx={{ fontSize: 18 }} />
            </button>
          </Tooltip>
          <Tooltip title='Laptop'>
            <button
              className={`${styles.deviceBtn} ${previewDevice === 'laptop' ? styles.deviceBtnActive : ''}`}
              onClick={() => setPreviewDevice('laptop')}
            >
              <LaptopIcon sx={{ fontSize: 18 }} />
            </button>
          </Tooltip>
          <Tooltip title='Mobile'>
            <button
              className={`${styles.deviceBtn} ${previewDevice === 'mobile' ? styles.deviceBtnActive : ''}`}
              onClick={() => setPreviewDevice('mobile')}
            >
              <SmartphoneIcon sx={{ fontSize: 18 }} />
            </button>
          </Tooltip>
        </div>

        <div className={styles.headerRight}>
          <Tooltip title='Undo (Ctrl+Z)'>
            <span>
              <button className={styles.undoRedoBtn} onClick={undo} disabled={!canUndo}>
                <UndoIcon sx={{ fontSize: 18 }} />
              </button>
            </span>
          </Tooltip>
          <Tooltip title='Redo (Ctrl+Y)'>
            <span>
              <button className={styles.undoRedoBtn} onClick={redo} disabled={!canRedo}>
                <RedoIcon sx={{ fontSize: 18 }} />
              </button>
            </span>
          </Tooltip>
          <div className={styles.headerDivider} />
          <Chip
            label={saveStateLabel[saveState] || ''}
            size='small'
            className={`${styles.saveChip} ${saveStateClass[saveState] ?? styles.saveAutosaveOn}`}
          />
        </div>
      </div>

      {/* Workspace */}
      <div
        className={styles.workspace}
        onClick={() => {
          setSelectedBlockInfo(null);
          setEditingSectionId(null);
        }}
      >
        {/* Left: Form Settings */}
        <div
          className={`${styles.settingsPanel} ${
            selectedBlock !== null || editingSectionId !== null ? styles.settingsPanelCollapsed : ''
          }`}
        >
          <div className={styles.settingsPanelScroll}>
            <Typography className={styles.settingsPanelTitle}>Form Settings</Typography>

            <div className={styles.settingsField}>
              <label className={styles.settingsLabel}>Form Title *</label>
              <input
                className={styles.settingsInput}
                value={settings.title}
                onChange={(e) => updateSettings({ title: e.target.value })}
                placeholder='Enter form title'
              />
            </div>

            <div className={styles.settingsField}>
              <label className={styles.settingsLabel}>Form Instructions</label>
              <input
                className={styles.settingsInput}
                value={settings.headerSubtitle}
                onChange={(e) => {
                  const val = e.target.value;
                  updateSettings({ headerSubtitle: val });
                }}
                placeholder='Please read carefully and sign.'
              />
            </div>

            <div className={styles.settingsField}>
              <label className={styles.settingsLabel}>
                Form Description{' '}
                <span style={{ fontWeight: 400, color: 'var(--app-text-muted)' }}>
                  ({(settings.subtitle ?? '').length}/50)
                </span>
              </label>
              <input
                className={styles.settingsInput}
                value={settings.subtitle ?? ''}
                maxLength={50}
                onChange={(e) => updateSettings({ subtitle: e.target.value })}
                placeholder='Short description (optional)'
              />
              <span style={{ fontSize: 11, color: 'var(--app-text-muted)', marginTop: 2 }}>
                Shown beneath the form title in Settings.
              </span>
            </div>

            <div className={styles.settingsField}>
              <label className={styles.settingsLabel}>Intro Text</label>
              <textarea
                className={styles.settingsInput}
                rows={3}
                value={settings.intro}
                onChange={(e) => updateSettings({ intro: e.target.value })}
                placeholder='Optional intro shown at the top of the form'
              />
            </div>

            {/* Section list — drag to reorder with live preview */}
            {sections.length > 0 && (
              <div className={styles.sectionOutline}>
                <div className={styles.sectionOutlineHeader}>
                  <Typography className={styles.settingsPanelTitle}>Sections</Typography>
                  <button className={styles.sectionOutlineAddBtn} onClick={addSection} title='Add Section'>
                    <AddIcon sx={{ fontSize: 14 }} />
                    Add
                  </button>
                </div>
                {sectionDisplayOrder.map((section, displayIdx) => {
                  const originalIdx = sections.indexOf(section);
                  const isDragging = dragSectionFrom === originalIdx;
                  return (
                    <div
                      key={section._id}
                      className={`${styles.sectionOutlineRow} ${isDragging ? styles.sectionOutlineDragging : ''}`}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('sectionDragIdx', String(originalIdx));
                        setDragSectionFrom(originalIdx);
                        setDragSectionOver(displayIdx);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragSectionOver(displayIdx);
                      }}
                      onDragEnd={() => {
                        setDragSectionFrom(null);
                        setDragSectionOver(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const fromIdx = parseInt(e.dataTransfer.getData('sectionDragIdx'), 10);
                        if (!isNaN(fromIdx) && fromIdx !== displayIdx) reorderSections(fromIdx, displayIdx);
                        setDragSectionFrom(null);
                        setDragSectionOver(null);
                      }}
                    >
                      <span className={styles.sectionOutlineDrag}>⠿</span>
                      <span className={styles.sectionOutlineNum}>{displayIdx + 1}</span>
                      <input
                        className={styles.sectionOutlineInput}
                        value={section.heading}
                        onChange={(e) => _updateSection(section._id, { heading: e.target.value })}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className={styles.settingsPanelBottom}>
            <button className={styles.deleteFormBtn} onClick={() => setDeleteConfirmOpen(true)}>
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              Delete Form
            </button>
            <div className={styles.settingsPanelLogo}>
              <img
                src={resolvedThemeMode === 'dark' ? '/logo-form-builder-dark.svg' : '/logo-form-builder.svg'}
                alt='Form Builder'
                draggable={false}
              />
            </div>
          </div>
        </div>

        {deleteConfirmOpen && (
          <div className={styles.deleteOverlay}>
            <div className={styles.deleteDialog}>
              <div className={styles.deleteDialogTitle}>Are You Sure?</div>
              <div className={styles.deleteDialogSubtext}>
                Deleting this form is permanent and cannot be undone. All associated content and settings will be
                permanently removed.
              </div>
              <div className={styles.deleteDialogActions}>
                <button className={styles.deleteDialogCancel} onClick={() => setDeleteConfirmOpen(false)}>
                  Cancel
                </button>
                <button
                  className={styles.deleteDialogConfirm}
                  onClick={() => {
                    if (!formId) return;
                    deleteCustomConsentForm(formId)
                      .then(() => {
                        enqueueSnackbar('Form deleted', { variant: 'success' });
                        navigate('/settings#teleconsent');
                      })
                      .catch(() => {
                        enqueueSnackbar('Failed to delete form', { variant: 'error' });
                        setDeleteConfirmOpen(false);
                      });
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Center: Live Form Preview (actual ConsentFormRenderer) */}
        <div className={styles.canvas}>
          <div
            className={styles.previewWrapper}
            style={{ maxWidth: deviceMaxWidth[previewDevice] }}
            onClick={(e) => e.stopPropagation()}
          >
            {sections.length === 0 ? (
              <div
                className={styles.emptyCanvas}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const blockType = e.dataTransfer.getData('blockType');
                  if (blockType) {
                    addSection();
                  }
                }}
              >
                <div className={styles.emptyIcon}>
                  <svg width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.2'>
                    <rect x='3' y='3' width='18' height='18' rx='2' />
                    <line x1='3' y1='9' x2='21' y2='9' />
                    <line x1='9' y1='21' x2='9' y2='9' />
                  </svg>
                </div>
                <Typography className={styles.emptyText}>Drag &amp; Drop Components Here</Typography>
                <Typography className={styles.emptySubtext}>or click "Add Section" below to get started</Typography>
              </div>
            ) : (
              <ConsentFormRenderer
                definition={liveDefinition}
                isBlankPreview={true}
                builderConfig={builderConfig}
                providerName={VARIABLE_PREVIEW_CONTEXT.providerName}
                providerFirstName={VARIABLE_PREVIEW_CONTEXT.providerFirstName}
                providerLastName={VARIABLE_PREVIEW_CONTEXT.providerLastName}
                providerTitle={VARIABLE_PREVIEW_CONTEXT.providerTitle}
                providerSuffix={VARIABLE_PREVIEW_CONTEXT.providerSuffix}
                providerSpeciality={VARIABLE_PREVIEW_CONTEXT.providerSpeciality}
                providerRoomURL={VARIABLE_PREVIEW_CONTEXT.providerRoomURL}
                providerEmail={VARIABLE_PREVIEW_CONTEXT.providerEmail}
                organizationName={VARIABLE_PREVIEW_CONTEXT.organizationName}
                clientLabel='Patient'
              />
            )}

            <button className={styles.addSectionBtn} onClick={addSection}>
              <AddIcon sx={{ fontSize: 18 }} />
              Add Section
            </button>
          </div>
        </div>

        {/* Right: Component Palette (hidden when block config drawer is open) */}
        <ComponentPalette />
      </div>

      <BlockConfigDrawer
        block={selectedBlock}
        onClose={() => setSelectedBlockInfo(null)}
        onUpdate={(patch) => {
          if (selectedBlockInfo) {
            const section = sections.find((s) => s._id === selectedBlockInfo.sectionId);
            const block = section?.blocks[selectedBlockInfo.blockIndex];
            if (block) updateBlock(selectedBlockInfo.sectionId, block._id, patch);
          }
        }}
      />
      <SectionConfigDrawer
        section={editingSectionId ? sections.find((s) => s._id === editingSectionId) ?? null : null}
        onClose={() => setEditingSectionId(null)}
        onUpdate={(patch) => {
          if (editingSectionId) _updateSection(editingSectionId, patch);
        }}
      />
    </div>
  );
}
